import {APPROVAL_MODES, isSessionApprovalMode, type SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'

import type {EnterpriseManagedSettings} from '@github-ui/copilot-chat-types'

export interface EnterpriseManagedSettingsInput {
  enterpriseManagedSettings?: EnterpriseManagedSettings
  enterpriseManagedSettingsUrl?: string
  currentUserLogin?: string
}

export const ENTERPRISE_MANAGED_SETTINGS_ERROR: EnterpriseManagedSettings = {
  status: 'error',
  permissions: {availableApprovalModes: ['manual']},
}

export const ENTERPRISE_MANAGED_SETTINGS_LOADING_MESSAGE = 'Loading tool permissions'
export const ENTERPRISE_MANAGED_SETTINGS_ERROR_MESSAGE =
  'Tool permissions could not be loaded. Only Always ask is available.'

export function parseEnterpriseManagedSettings(value: unknown): EnterpriseManagedSettings {
  if (typeof value !== 'object' || value === null || !('status' in value)) {
    throw new Error('Invalid enterprise-managed settings')
  }
  if (value.status === 'error') return ENTERPRISE_MANAGED_SETTINGS_ERROR
  if (value.status !== 'none' && value.status !== 'ready' && value.status !== 'pending') {
    throw new Error('Invalid enterprise-managed settings status')
  }
  if (!('permissions' in value) || typeof value.permissions !== 'object' || value.permissions === null) {
    throw new Error('Invalid enterprise-managed settings permissions')
  }
  const permissions = value.permissions
  if (!('availableApprovalModes' in permissions)) throw new Error('Missing enterprise-managed approval modes')
  const modes = permissions.availableApprovalModes
  if (
    !Array.isArray(modes) ||
    !modes.every(isSessionApprovalMode) ||
    new Set(modes).size !== modes.length ||
    !modes.includes('manual') ||
    (value.status === 'none' && modes.length !== APPROVAL_MODES.length) ||
    (value.status === 'pending' && modes.length !== 1)
  ) {
    throw new Error('Invalid enterprise-managed approval modes')
  }
  return {status: value.status, permissions: {availableApprovalModes: modes}}
}

export function resolveEnterpriseManagedSettingsApprovalMode(
  preferred: SessionApprovalMode,
  settings: EnterpriseManagedSettings | undefined,
): SessionApprovalMode | undefined {
  if (!settings) return preferred
  if (settings.status === 'pending') return undefined
  if (settings.status === 'error') return 'manual'
  const modes = settings.permissions.availableApprovalModes
  if (modes.includes(preferred)) return preferred
  if (modes.includes('assisted')) return 'assisted'
  if (modes.includes('manual')) return 'manual'
  return undefined
}

export function requireEnterpriseManagedSettingsApprovalMode(
  preferred: SessionApprovalMode,
  settings: EnterpriseManagedSettings | undefined,
): SessionApprovalMode {
  const mode = resolveEnterpriseManagedSettingsApprovalMode(preferred, settings)
  if (!mode) throw new Error('Tool permissions are not ready. Wait for them to load before starting a session.')
  return mode
}
